/**
 * Shared mic button for Pre-Consultation, Patient Visit, and Management.
 * Uses AmbientListeningContext so start/stop and listening state are in sync across all three tabs.
 * Same look as the original Patient Visit mic: mic_icon when idle, MicOff + pulse when listening.
 */
import { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Paper, Tooltip, Alert, CircularProgress, LinearProgress } from '@mui/material';
import { useAmbientListening } from '../../contexts/AmbientListeningContext';
import { useSession } from '../../contexts/SessionContext';
import { useUser } from '../../contexts/UserContext';
import AudioSourceDialog, { AudioSourceType } from '../Consultation/AudioSourceDialog';
import CabinSelectorDialog from '../Consultation/CabinSelectorDialog';

type Placement = 'sticky' | 'fixed';

interface AmbientMicButtonProps {
  /** 'sticky' = in-flow bottom-left of form (default); 'fixed' = floating bottom-left of viewport */
  placement?: Placement;
}

const stateConfig = {
  idle: {
    color: '#1565c0',
    backgroundColor: '#e3f2fd',
    pulseAnimation: false,
    label: 'Click to start listening',
  },
  listening: {
    color: '#009688',
    backgroundColor: '#e0f2f1',
    pulseAnimation: true,
    label: 'I am listening - Click to stop',
  },
  processing: {
    color: '#ff9800',
    backgroundColor: '#fff8e1',
    pulseAnimation: true,
    label: 'Processing...',
  },
};

export default function AmbientMicButton({ placement = 'sticky' }: AmbientMicButtonProps) {
  const { session, formSessionOverride, createSession, loading } = useSession();
  const { patient } = useUser();
  const { state, processingProgress, awaitingWsClose, error, clearError, startListening, stopListening } =
    useAmbientListening();
  const [audioSourceDialogOpen, setAudioSourceDialogOpen] = useState(false);
  const [cabinSelectorOpen, setCabinSelectorOpen] = useState(false);
  const [backgroundToastOpen, setBackgroundToastOpen] = useState(false);

  // Show toast when an active listening session stops (listening → idle).
  const prevStateRef = useRef(state);
  useEffect(() => {
    const prev = prevStateRef.current;
    prevStateRef.current = state;
    if (prev === 'listening' && state === 'idle') {
      setBackgroundToastOpen(true);
      const t = window.setTimeout(() => setBackgroundToastOpen(false), 2500);
      return () => window.clearTimeout(t);
    }
  }, [state]);

  useEffect(() => {
    if (formSessionOverride) stopListening();
  }, [formSessionOverride, stopListening]);

  const hasActiveSession = !!session && session.status === 'active';
  const config = stateConfig[state];

  const startForSession = (sessionId: string, audioType: 'browser' | 'hardware') => {
    startListening(sessionId, audioType);
    // Tell RightPanel to switch to Current Visit -> Live feed when mic starts.
    window.dispatchEvent(new CustomEvent('right-panel:show-live-feed'));
  };

  const createSessionFromMic = async (audioSource: AudioSourceType, cabinId?: string) => {
    try {
      const newSession = await createSession(audioSource, cabinId);
      if (newSession?.session_id) {
        const audioType = (newSession.audio_source_type as 'browser' | 'hardware') || audioSource;
        startForSession(newSession.session_id, audioType);
      }
    } catch {
      // SessionContext/AmbientListening already surface errors in UI.
    }
  };

  const handleAudioSourceSelect = (audioSource: AudioSourceType) => {
    setAudioSourceDialogOpen(false);
    if (audioSource === 'hardware') {
      setCabinSelectorOpen(true);
      return;
    }
    createSessionFromMic(audioSource);
  };

  const handleCabinSelect = (cabinId: string) => {
    setCabinSelectorOpen(false);
    createSessionFromMic('hardware', cabinId);
  };

  const handleToggle = () => {
    if (state === 'idle') {
      if (hasActiveSession && session?.session_id) {
        const audioType = (session.audio_source_type as 'browser' | 'hardware') || 'browser';
        startForSession(session.session_id, audioType);
      } else if (patient && !formSessionOverride) {
        // Restore old UX: mic can initiate Start Visit flow via audio source chooser.
        setAudioSourceDialogOpen(true);
      }
    } else {
      stopListening();
    }
  };

  const canStartFromMic = hasActiveSession || !!patient;
  const isDisabled =
    !!formSessionOverride ||
    !canStartFromMic ||
    state === 'processing' ||
    awaitingWsClose ||
    loading;

  const wrapperSx =
    placement === 'fixed'
      ? {
          position: 'fixed' as const,
          bottom: 24,
          left: 24,
          zIndex: 1300,
        }
      : {
          position: 'sticky' as const,
          bottom: 8,
          left: 8,
          mt: 2,
          alignSelf: 'flex-start',
        };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, ...wrapperSx }}>
      {error && (
        <Alert severity="error" onClose={clearError} sx={{ mb: 1, maxWidth: 320 }}>{error}</Alert>
      )}
      {state === 'listening' && (
        <Paper
          elevation={2}
          sx={{
            px: 1.5,
            py: 0.5,
            bgcolor: 'rgba(0,0,0,0.8)',
            color: 'white',
            borderRadius: 2,
            fontSize: '0.75rem',
            whiteSpace: 'nowrap',
          }}
        >
          I am listening
        </Paper>
      )}
      {backgroundToastOpen && state === 'idle' && (
        <Paper
          elevation={2}
          sx={{
            px: 1.5,
            py: 0.5,
            bgcolor: 'rgba(255,214,0,0.92)',
            color: 'rgba(0,0,0,0.8)',
            borderRadius: 2,
            fontSize: '0.75rem',
            whiteSpace: 'nowrap',
          }}
        >
          Processing…
        </Paper>
      )}
      {state === 'processing' && (
        <Paper
          elevation={2}
          sx={{
            px: 1.5,
            py: 0.75,
            bgcolor: 'rgba(255,152,0,0.95)',
            color: 'white',
            borderRadius: 2,
            fontSize: '0.75rem',
            minWidth: 200,
            maxWidth: 280,
          }}
        >
          <Box component="span" sx={{ display: 'block', whiteSpace: 'normal', lineHeight: 1.35 }}>
            {processingProgress?.label ?? 'Processing conversation…'}
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, Math.max(0, processingProgress?.percent ?? 8))}
            sx={{
              mt: 0.75,
              height: 5,
              borderRadius: 1,
              bgcolor: 'rgba(255,255,255,0.35)',
              '& .MuiLinearProgress-bar': { bgcolor: 'white' },
            }}
            aria-label="Processing progress"
          />
        </Paper>
      )}

      <Tooltip
        title={
          formSessionOverride
            ? 'Microphone is disabled while editing a past visit'
            : canStartFromMic
              ? state === 'idle'
                ? hasActiveSession
                  ? 'Start ambient listening'
                  : 'Select audio source and start visit'
                : state === 'processing'
                  ? processingProgress?.label ?? 'Processing conversation…'
                  : awaitingWsClose
                    ? 'Finishing…'
                    : 'Stop listening'
              : 'Start Visit to use voice input'
        }
      >
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <IconButton
            onClick={handleToggle}
            disabled={isDisabled}
            sx={{
              width: 64,
              height: 64,
              bgcolor: state === 'idle' ? config.backgroundColor : config.color,
              color: state === 'idle' ? config.color : 'white',
              boxShadow: config.pulseAnimation
                ? `0 0 20px ${config.color}, 0 4px 12px rgba(0,0,0,0.3)`
                : '0 4px 12px rgba(0,0,0,0.3)',
              border: `3px solid ${config.color}`,
              transition: 'all 0.3s ease',
              animation: config.pulseAnimation ? 'pulseFloat 2s ease-in-out infinite' : 'none',
              position: 'relative',
              zIndex: 1,
              '&:hover': {
                transform: 'scale(1.1)',
                boxShadow: `0 0 30px ${config.color}, 0 6px 16px rgba(0,0,0,0.4)`,
              },
              '&.Mui-disabled': {
                bgcolor: '#e0e0e0',
                borderColor: '#bdbdbd',
                color: '#757575',
                boxShadow: 'none',
              },
              '@keyframes pulseFloat': {
                '0%, 100%': {
                  transform: 'scale(1)',
                  boxShadow: `0 0 20px ${config.color}, 0 4px 12px rgba(0,0,0,0.3)`,
                },
                '50%': {
                  transform: 'scale(1.05)',
                  boxShadow: `0 0 30px ${config.color}, 0 6px 16px rgba(0,0,0,0.4)`,
                },
              },
            }}
          >
            {state === 'idle' ? (
              <Box
                component="img"
                src="/assets/mic_start_icon.png"
                alt="Start Listening"
                sx={{ width: 36, height: 36, objectFit: 'contain' }}
              />
            ) : state === 'processing' ? (
              <CircularProgress size={32} sx={{ color: 'white' }} aria-label="Processing conversation" />
            ) : (
              <Box
                component="img"
                src="/assets/mic_icon.png"
                alt="Listening"
                sx={{ width: 36, height: 36, objectFit: 'contain' }}
              />
            )}
          </IconButton>

          {config.pulseAnimation && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: 64,
                height: 64,
                borderRadius: '50%',
                border: `2px solid ${config.color}`,
                pointerEvents: 'none',
                animation: 'pulseRing 2s ease-in-out infinite',
                '@keyframes pulseRing': {
                  '0%': { opacity: 1, transform: 'scale(1)' },
                  '100%': { opacity: 0, transform: 'scale(1.5)' },
                },
              }}
            />
          )}
        </Box>
      </Tooltip>

      <AudioSourceDialog
        open={audioSourceDialogOpen}
        onClose={() => setAudioSourceDialogOpen(false)}
        onSelect={handleAudioSourceSelect}
      />
      <CabinSelectorDialog
        open={cabinSelectorOpen}
        onClose={() => { setCabinSelectorOpen(false); setAudioSourceDialogOpen(true); }}
        onSelect={handleCabinSelect}
      />

    </Box>
  );
}
